// shared/src/prompts/m-inductive-forecast.ts
// Chain V2 — inductive forecaster (design-lock Pro; runs Flash per the
// 2026-06-16 cost revision). Per design-notes L19/L19a/L20.
//
// PATH-DEPENDENT forecaster. BLIND to today's macro payload, news, prices.
// Inputs: historical synthesis-row trajectory window per indicator + realized
// accuracy + today_iso. Output: predicted_observables[] for today, derived
// purely by extrapolating the historical pattern.
//
// Anchoring protection per L19a: this LLM never sees today's payload — pattern
// extraction from history is its only input. It is a PEER hypothesis at
// m.synthesis (3-way adjudication post-floor); contrarian + consensus paths
// NEVER see this output.
//
// Per L20: output persists to dedicated `inductive_forecasts` table, NOT
// market_emit (data model is fundamentally different — predictions-only,
// no mechanism prose, indexed by forecast_date).

import { z } from 'zod';
import { DirectionEnum } from '../resolvers/llm-enum-coerce.js';
import { llmStringItem } from '../resolvers/llm-string-coerce.js';
import { llmNumber } from '../resolvers/llm-number-coerce.js';
import { tolerantRowArray } from '../resolvers/llm-row-drop.js';
import type { InductiveTrajectory } from '../translators/inductive-trajectory-build.js';

export const VERSION = '1.4.0';   // 2026-08-07 — predicted_observables per-ROW omission hardening (tolerantRowArray: drop malformed observables; .min(1)→.default([]); stage skips persist when empty). 2026-07-10 — trajectory_breaks_detected .default([]) (omission-safe; rule 7 "may be empty / NEVER OMIT" → default the omitted key). 2026-06-11 — schema rigidity hardening: change_pct/_ci_low/_ci_high + trajectory_confidence llmNumber-coerced (string "3.5%"/"0.55" → number; range stays); trend_summary .min(1)→z.string() (cosmetic prose; inductive writer is defensive — no validator twin). Writes to inductive_forecasts, not market-emit. See docs/llm-schema-rigidity-hardening-2026-06-11.md. // Prior: 1.1.0 (2026-05-25 — trajectory_breaks_detected required-may-be-empty).
export const STEP    = 'm.inductive.forecast';
export const MODEL   = 'deepseek-v4-flash';    // Flash per cost revision 2026-06-16 (blind peer-hypothesis forecaster; design-lock Pro, deviation in _design-lock.ts → inductive-forecasters-pro-to-flash-2026-06-16-brief-revision.md)
// 2026-08-01 — raised 10000 → 24000. The 2026-07-31 M-stage sweep raised the
// analysts/refines to 24000 but skipped this forecaster; the 30-day headroom
// review found it the tightest LIVE reasoning step at 8409/10000 (84%),
// reasoning 7656 — same Flash reasoning-drift regime that truncated its M-stage
// siblings. Matches the family ceiling. Ceiling, not target — free on early
// stops. docs/audits/2026-08-01-token-cap-headroom-review.md
export const MAX_TOKENS = 32000;   // 2026-08-01 blanket truncation-proofing (was 24000)
export const ROLE    = 'inductive' as const;
export const DESC    = 'Forecasts macro observables from prediction-history trajectory, blind to today\'s payload' as const;   // #45 registry — one-line operator description

// L19 output: predicted_observables + trend_summary + trajectory_confidence
// + trajectory_breaks_detected[]. NO mechanism prose (inductive cannot
// ground mechanism without payload; forcing it would invite hallucination).
export const OutputSchema = z.object({
  // 2026-08-07: per-ROW omission hardening (docs/agreement-matrix-row-omission-hardening-2026-08-07-brief-revision.md).
  // A malformed observable (missing indicator/direction, non-numeric change) is
  // unusable → DROP it + log, rather than hard-kill the whole brief on one bad row.
  // .min(1) relaxed to .default([]) via tolerantRowArray: if EVERY row is bad the
  // forecast is empty, which the stage treats as "no inductive this brief" (skips
  // the persist → synthesis runs 2-way), matching the no-trajectory early-return.
  predicted_observables: tolerantRowArray(z.object({
    indicator:      z.string().min(1),
    direction:      DirectionEnum,
    change_pct:     llmNumber(),
    change_ci_low:  llmNumber(),
    change_ci_high: llmNumber(),
  }), 'm.inductive.forecast.predicted_observables'),
  /** ≤60w characterizing WHAT pattern was extrapolated, not WHY. 2026-06-11: .min(1) dropped (cosmetic prose). */
  trend_summary:             z.string(),
  /** 0-1 confidence in the trajectory continuing — derived from trajectory shape consistency. */
  trajectory_confidence:     llmNumber(z.number().min(0).max(1)),
  /** Indicators whose recent pattern broke from prior trajectory (regime shift
   *  signal). Items coerced string|{indicator,description}→string via the shared
   *  llmStringItem translation-layer coercer (the forecaster emits both shapes;
   *  rigid string[] killed the 2026-06-10T19:00Z brief on USO/TNX object breaks). */
  trajectory_breaks_detected: z.array(llmStringItem).default([]),   // 2026-07-10: prompt rule 7 says "may be empty / NEVER OMIT" — default an omitted key to [] (no regime break) rather than fail the brief
}).passthrough();
export type Output = z.infer<typeof OutputSchema>;

export const SYSTEM = `# INDUCTIVE FORECASTER (V2 — Pro tier; path-dependent; blind to today's payload)

## ROLE
You are an INDUCTIVE FORECASTER. You see ONLY:
  · Historical trajectory of synthesis predictions per indicator (window of N briefs)
  · Realized accuracy (what actually happened on the resolved horizons)
  · Today's date (today_iso)

You DO NOT see today's macro payload, today's news, today's prices, or any contrarian / consensus / synthesis output from today. Your forecast is derived PURELY from extrapolating the historical pattern.

Your forecast becomes a PEER HYPOTHESIS to today's market chain at synthesis stage. The synthesis LLM treats your output as a path-dependent (history-only) competing view alongside contrarian (today-payload-anchored) + consensus (today-payload-anchored).

## STRUCTURAL ROLE (why you exist)
The main market chain is PATH-INDEPENDENT — it reasons from today's snapshot regardless of how prices got there. To offset that blind spot, you provide a PATH-DEPENDENT forecast. When the snapshot reads risk-on but the trajectory has been trending toward risk-off for 6 weeks, your forecast surfaces that. When the trajectory has been stable but the snapshot reads volatile, your forecast holds the stable line.

## INPUTS YOU RECEIVE (user message)
- TODAY_ISO (the date you are forecasting FOR)
- WINDOW_N requested (e.g. 30) + WINDOW_ACTUAL (synthesis rows available, may be less)
- PER-INDICATOR TRAJECTORIES:
  - Indicator (e.g. $BTC, TNX, SPY)
  - Points (chronological order, oldest first):
    - forecast_date, predicted_pct, actual_pct (null when not yet realized), classification (hit / partial / miss / null)
  - mean_direction (cheap regime cue across window)
  - volatility (std-dev of predicted_pct)
  - hit_rate (accuracy over resolved points)

## OUTPUT (strict JSON; first char='{'; last char='}'; no commentary)
{
  "predicted_observables": [
    {
      "indicator":      "$BTC",
      "direction":      "up" | "down" | "flat",
      "change_pct":     <signed percent move from today's level over a roughly 14-day horizon>,
      "change_ci_low":  <80% CI lower bound, signed percent>,
      "change_ci_high": <80% CI upper bound, signed percent>
    }
    // ≥1 entry. Cover at least the indicators with non-trivial trajectory history.
  ],
  "trend_summary":             "<≤60w characterizing WHAT pattern was extrapolated. Cite trajectory shape, not mechanism. e.g. 'rates 4w bullish trend with widening volatility; crypto sideways consolidation; equity beta compression.' NO 'because the Fed will pivot' — you don't know the Fed exists.>",
  "trajectory_confidence":     0.55,
  "trajectory_breaks_detected": []
}

## RULES (strict)
1. First char='{'. Last char='}'. No text outside JSON. NO mechanism prose anywhere. Mechanism requires payload; you have none.
2. predicted_observables: at least one entry. Cover indicators with ≥3 trajectory points where possible.
3. DIRECTION + CHANGE_PCT consistency: 'up' → change_pct ≥ +0.1; 'down' → change_pct ≤ -0.1; 'flat' → |change_pct| < 0.5.
4. CI BOUNDS: change_ci_low ≤ change_pct ≤ change_ci_high. WIDER CIs when trajectory volatility is high; tighter when trajectory is consistent.
5. trend_summary describes WHAT pattern, NOT WHY. Cite trajectory shape: 'sustained uptrend', 'mean-reversion oscillation', 'consolidation', 'break from prior trend.' NEVER invoke today's payload, today's news, regime labels you couldn't derive from history alone, or specific Fed/CPI/event names (you don't know these exist).
6. trajectory_confidence (0-1, two decimals): score conviction in the TRAJECTORY itself continuing. Two-decimal calibration: ≥0.70 when 3+ consecutive same-direction points; 0.40-0.69 when partial pattern; ≤0.39 when trajectory is noisy / breaks recently. 0.50 is valid when the trajectory is genuinely balanced — anti-hedge rule removed (Session 5T D36).
7. trajectory_breaks_detected: REQUIRED field (may be empty array). Name any indicators whose MOST RECENT 1-2 points deviate from the trajectory shape. Emit [] when no breaks. This is the "regime shift" signal the synthesis LLM may weight. NEVER OMIT the field.
8. WINDOW_ACTUAL < window_n is OK. Your forecast can still surface usable signal off a smaller window; trajectory_confidence should reflect lower N.
9. NO HEDGING. Pick direction + change_pct decisively per indicator.

## ANTI-FABRICATION GUARD
You have NO knowledge of today's news, today's prices, today's macro events. If you find yourself wanting to write 'CPI is coming' or 'Fed-speak suggests' or any forward-looking mechanism — STOP. That belongs in the contrarian / consensus paths, which DO have today's payload. Your job is pure pattern extrapolation.
`;

export interface MInductiveForecastInput {
  readonly trajectory:  InductiveTrajectory;
}

export function buildUserPrompt(input: MInductiveForecastInput): string {
  const t = input.trajectory;
  const lines: string[] = [
    `TODAY_ISO: ${t.today_iso}`,
    `WINDOW_N (requested): ${t.window_n}`,
    `WINDOW_ACTUAL: ${t.window_actual}`,
    '',
    'PER-INDICATOR TRAJECTORIES:',
  ];
  for (const tr of t.trajectories) {
    lines.push('');
    lines.push(`[${tr.indicator}]`);
    lines.push(`  mean_direction: ${tr.mean_direction}  volatility: ${tr.volatility.toFixed(2)}  hit_rate: ${tr.hit_rate === null ? 'null' : tr.hit_rate.toFixed(2)}`);
    lines.push('  points (oldest → newest):');
    for (const p of tr.points) {
      const actual = p.actual_pct === null ? 'pending' : `${p.actual_pct.toFixed(2)}%`;
      const cls    = p.classification ?? '—';
      lines.push(`    ${p.forecast_date}  pred=${p.predicted_pct.toFixed(2)}%  actual=${actual}  class=${cls}`);
    }
  }
  if (t.trajectories.length === 0) {
    lines.push('  (no indicator trajectories — insufficient history)');
  }
  lines.push('');
  lines.push('REQUEST: emit_m_inductive_forecast');
  lines.push('');
  lines.push('Emit a SINGLE JSON object matching the schema. NO mechanism prose; trajectory shape only. Anchoring guard: you have no today-payload knowledge.');
  return lines.join('\n');
}
